import { LogLevel } from "../shared/logging";
export { LogLevel };

let config;
const getConfig = () => {
    if (!config) {
        // we initialize config based on env vars
        const env = process.env;
        let level = LogLevel.Info;
        let configOk;

        // check log level from env var
        let configLevelName = (env.REACT_APP_LOG_LEVEL || env.LOG_LEVEL || "").toLowerCase();
        if (configLevelName) {
            const configLevel = getLogLevel(configLevelName);
            configOk = !!configLevel;
            if (configOk) {
                level = configLevel;
            }
        }

        config = {
            level,
            levelName: getLogLevelName(level),
            prefix: env.REACT_APP_LOG_PREFIX || (env.ORDERS_NAME && `[${env.ORDERS_NAME.toUpperCase()}]`) || "",
        }

        if (configLevelName) {
            // we do this after setting config obj above, so the log funcs will work/not spin out in infinite loop cuz they rely on this function
            if (configOk) {
                info('Log Level Configured:', config.levelName);
            } else {
                warn(`Invalid log level '${configLevelName}' supplied in REACT_APP_LOG_LEVEL. Defaulting to Info.`);
            }
        }
    }
    return config;
}

const getLogLevel = (forLevelName = getConfig().levelName) => {
    forLevelName = forLevelName.toLowerCase();
    return LogLevel[Object.keys(LogLevel).find(level => level.toLowerCase() === forLevelName)];
}
const getLogLevelName = (forLevel = getConfig().level) => Object.keys(LogLevel).find(level => LogLevel[level] === forLevel) || 'Unknown';

const noop = () => { };

export const isDebugEnabled = () => getConfig().level >= LogLevel.Debug;

// because we allow changing the log level, we have to evaluate it on the fly
// this returns a log function that will either log or not log depending on current log level, using the specified prefix and logging func
const getLoggingFunc = (desiredLogLevel) => {
    const cfg = getConfig();
    if (cfg.level >= desiredLogLevel) {
        // NOTE: IE will silently cease executing script if you try to pass/use a pointer to its console log functions when dev tools not open
        //   So instead of passing the desired logger or, e.g., using strategy pattern here, we use a switch (ugh)
        switch (desiredLogLevel) {
            case LogLevel.Error:
                return (...args) => { console.error(cfg.prefix, ...args); };
            case LogLevel.Warn:
                return (...args) => { console.warn(cfg.prefix, ...args); };
            case LogLevel.Info:
                return (...args) => { console.info(cfg.prefix, ...args); };
            case LogLevel.Debug:
                return (...args) => { console.log(cfg.prefix, 'DEBUG:', ...args); };
            case LogLevel.Verbose:
                return (...args) => { console.log(cfg.prefix, 'VERBOSE:', ...args); };
            default:
                return (...args) => { console.log(cfg.prefix, ...args); };
        }
    } else {
        return noop;
    }
}

// for each of these, we gather "rest" args passed in, get the appropriate log function for the current log level, then call it and spread the given args back out
export const verbose = (...args) => getLoggingFunc(LogLevel.Verbose)(...args);

export const debug = (...args) => getLoggingFunc(LogLevel.Debug)(...args);

export const info = (...args) => getLoggingFunc(LogLevel.Info)(...args);

export const warn = (...args) => getLoggingFunc(LogLevel.Warn)(...args);

export const error = (...args) => getLoggingFunc(LogLevel.Error)(...args);

/** Gets current logging configuration. */
export const getLoggingConfig = getConfig;

/**
 * Sets log level for browser logging.
 *
 * @param {LogLevel | string} logLevelOrName - one of the LogLevel enum values or the name of the desired log level
 * @param {string} - prefix to prepend to log messages; we will auto prepend [DEBUG] for debug logs and [VERBOSE] for verbose
 */
// NOTE: We allow this so the initializing app can tell us what log level to use, rather than using our own env var (for example).
export const setLogLevel = (logLevelOrName, appLogPrefix) => {
    const cfg = getConfig();
    if (appLogPrefix !== undefined) {
        cfg.prefix = String(appLogPrefix);
    }

    let newLevel;
    if (typeof logLevelOrName === 'number') {
        if (logLevelOrName >= LogLevel.Error && logLevelOrName <= LogLevel.Verbose) { // min/max boundaries
            newLevel = logLevelOrName;
        }
    } else {
        newLevel = getLogLevel(String(logLevelOrName));
    }

    if (newLevel) {
        cfg.level = newLevel;
        cfg.levelName = getLogLevelName(cfg.level);
        info('Log Level Set:', cfg.levelName);
    } else {
        warn(`Invalid Log Level passed to setLogLevel: '${logLevelOrName}'. Must be one of LogLevel enum options.`);
    }
}

